import { useMemo, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { CellValueChangedEvent, GetRowIdParams, RowClickedEvent } from 'ag-grid-enterprise'
import { Plus, Power, PowerOff, RefreshCcw, Send, Trash2 } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import { cn } from '@fortem-monorepo/js-utilities'
import { getT, toISO8601 } from '@fortem-monorepo/js-utilities'
import { AgGridWidget } from '@fortem-monorepo/react-ag'
import { CustomMenuItem } from '@fortem-monorepo/react-ag'
import { createContextMenu } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { Spinner } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@fortem-monorepo/react-ui'
import { ClientPricing } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { AddProductToClientsModal } from '@/components/dialogs/AddProductToClientsModalNew'
import { SendEmailsToClientsModal } from '@/components/dialogs/SendEmailsToClientsModalNew'
import { FLOW } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import {
  createClientsColDefs,
  createProductsColDefs,
} from '@/pages/flow/client-pricing-report/col-defs'
import { v2 as v2Mutations } from '@/react-query/mutations/client-pricing'

export interface ClientPricingReportPageProps {}

export const ClientPricingReportPage: React.FC<ClientPricingReportPageProps> = ({}) => {
  const [client_id, setClientId] = useState<number | undefined>()
  const { hasPermissions } = useUserContext()

  const [addingProductToClient, setAddingProductToClient] = useState(false)
  const [sendingEmails, setSendingEmails] = useState(false)

  const { Dialog: RemoveFromAllClientsModal, withConfirmation: confirmRemoveFromAllClients } =
    useConfirmModal({
      title: `Remove product from all clients?`,
    })

  const hasWriteAccess = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_CLIENT_PRICING],
  }))

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: ['client-pricing', 'clients', 'new'],
        queryFn: () => api.clientPricing.v2.getClients(),
        refetchInterval: 15_000,
      },
      {
        queryKey: ['client-pricing', 'products', 'new', { client_id }],
        queryFn: () => api.clientPricing.v2.getClientProducts({ client_id }),
        refetchInterval: 60_000,
        refetchOnMount: true,
      },
      {
        queryKey: ['client-pricing', 'data-presence'],
        queryFn: () => api.clientPricing.v2.getDataPresence(),
        refetchInterval: 60_000,
      },
    ],
  })

  const [clients, products, dataPresence] = queries

  const clientsData = useMemo(() => {
    return [{ client_name: 'All Products' }, ...(clients.data ?? [])]
  }, [clients.data])

  const clientsColDefs = useMemo(() => {
    return createClientsColDefs(hasWriteAccess)
  }, [hasWriteAccess])

  const productsColDefs = useMemo(() => {
    return createProductsColDefs()
  }, [hasWriteAccess])

  const selectedClientName = useMemo(() => {
    return (clients.data ?? []).find((client) => client.org_id === client_id)?.org_display_name
  }, [clients.data, client_id])

  const dataPresenceFormatted = useMemo(() => {
    const _data: Omit<ClientPricing.GetDataPresenceData[number], 'description'> =
      dataPresence.data?.[0] ?? {}

    const _return = [] as { date: string; present: boolean }[]

    const dates = Object.keys(dataPresence.data?.[0] ?? {})
      .filter((key) => {
        return key !== 'description'
      })
      .filter((key) => toISO8601(new Date(key)) !== toISO8601(getT()))

    for (let i = 0; i < dates.length; i++) {
      const date = dates[i]
      const present = _data[date] === 1

      _return.push({ date, present })
    }

    return _return.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }, [dataPresence.data])

  // ... Mutations
  const updateClientMutation = v2Mutations.useUpdateClientMutation()
  const removeProductFromClientMutation = v2Mutations.useRemoveProductFromClient()
  const removeProductFromAllClientsMutation = v2Mutations.useRemoveProductFromAllClients()

  const [product, setProduct] = useState<ClientPricing.V2.NewProduct>()

  const clientsContextMenu = createContextMenu<ClientPricing.V2.NewClient>((params) => {
    const isActive = params.node?.data?.active === 1

    return [
      {
        name: isActive ? 'Deactivate' : 'Activate',
        icon: renderToString(
          updateClientMutation.isPending ? (
            <Spinner size={16} />
          ) : isActive ? (
            <PowerOff size={16} />
          ) : (
            <Power size={16} />
          ),
        ),
        cssClasses: isActive
          ? ['hover:bg-red-600/10!', 'text-red-600']
          : ['hover:bg-green-600/10!', 'text-green-600'],
        disabled: updateClientMutation.isPending,
        action: () => {
          const newActive = params.node?.data?.active === 1 ? 0 : 1
          return updateClientMutation.mutateAsync({
            rowData: params.node?.data!,
            oldValue: params.node?.data?.active,
            newValue: newActive,
            updatedField: 'active',
          })
        },
      },
      'separator',
      'copy',
      'copyWithHeaders',
      'export',
    ]
  })

  const getProductsContextMenuItems = createContextMenu<ClientPricing.V2.NewProduct>((params) => {
    const isin = params.node?.data?.isin
    const instrument_id = params.node?.data?.instrument_id!

    return [
      ...(hasWriteAccess
        ? !!client_id
          ? ([
              {
                name: `Remove from ${selectedClientName} watchlist`,
                action: () =>
                  removeProductFromClientMutation.mutateAsync({ org_id: client_id, instrument_id }),
                icon: renderToString(<Trash2 size={16} />),
                cssClasses: ['hover:bg-red-600/10!', 'text-red-600'],
              },
              'separator',
            ] satisfies CustomMenuItem[])
          : ([
              {
                name: `Add to clients`,
                action: () => {
                  setProduct(params.node?.data)
                  setAddingProductToClient(true)
                },
                icon: renderToString(<Plus size={16} />),
              },
              'separator',
            ] satisfies CustomMenuItem[])
        : []),
      'separator',
      {
        name: `Delete all mappings for product: ${isin}`,
        action: () => {
          setProduct(params.node?.data)
          confirmRemoveFromAllClients(() =>
            removeProductFromAllClientsMutation.mutateAsync({ instrument_id }),
          )
        },
        icon: renderToString(<Trash2 size={16} />),
        cssClasses: ['hover:bg-red-600/10!', 'text-red-600'],
      },
      'separator',
      'copy',
      'copyWithHeaders',
      'copyWithGroupHeaders',
      'separator',
      'export',
    ]
  })

  return (
    <Page
      metadata={{ title: 'Client Pricing Report' }}
      permissions={FLOW.CLIENT_PRICING_REPORT.permissions}
    >
      <CanAccess permissions={[PERMISSIONS.WRITE_CLIENT_PRICING]}>
        <SendEmailsToClientsModal open={sendingEmails} onOpenChange={setSendingEmails} />
        <RemoveFromAllClientsModal>
          <p>
            You are about to remove product <span className={'font-medium'}>{product?.isin}</span>{' '}
            from all client watchlists. Are you sure?
          </p>
        </RemoveFromAllClientsModal>
        <AddProductToClientsModal
          open={addingProductToClient}
          onOpenChange={setAddingProductToClient}
          product={product}
        />
        <HeaderConfigBar>
          <div className="flex items-center gap-2">
            <Button size="sm" variant={'outline'} onClick={() => setSendingEmails(true)}>
              <Send />
              Send Emails
            </Button>
          </div>
        </HeaderConfigBar>
      </CanAccess>
      <HeaderConfigBar
        className={cn({
          'bg-green-100/60': dataPresenceFormatted.every((entry) => entry.present),
          'bg-red-100/60': dataPresenceFormatted.some((entries) => !entries.present),
        })}
      >
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={dataPresence.isFetching}
            className="p-1! h-auto! cursor-pointer"
            onClick={() => dataPresence?.refetch()}
          >
            {dataPresence.isFetching ? <Spinner /> : <RefreshCcw />}
          </Button>
          <span className="text-muted-foreground text-sm whitespace-nowrap">Data Presence</span>
          <div
            className={cn('flex items-center gap-1', {
              'animate-pulse bg-gray-300': dataPresence.isLoading,
            })}
          >
            {dataPresenceFormatted.map((data) => {
              return (
                <Tooltip key={data.date}>
                  <TooltipTrigger>
                    <div
                      key={data.date}
                      className={cn('aspect-square size-5 rounded-xs border border-gray-400/60', {
                        'bg-red-300': !data.present,
                        'bg-green-300': data.present,
                      })}
                    />
                  </TooltipTrigger>
                  <TooltipContent>{data.date}</TooltipContent>
                </Tooltip>
              )
            })}
          </div>
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'horizontal'}>
        <ResizablePanel>
          <AgGridWidget
            query={clients}
            widget={{ exposed: true, className: 'h-full' }}
            widgetHeader={{ title: `Clients` }}
            grid={{
              updating: updateClientMutation.isPending,
              getContextMenuItems: clientsContextMenu,
              rowData: clientsData,
              columnDefs: clientsColDefs,
              domLayout: 'normal',
              autoSizeStrategy: { type: 'fitCellContents' },
              getRowId: (params: GetRowIdParams<ClientPricing.V2.NewClient>) => {
                return params.data.org_id?.toString()
              },
              onRowClicked: (params: RowClickedEvent<ClientPricing.V2.NewClient>) => {
                setClientId(params.data?.org_id)
              },
              onCellValueChanged: (params: CellValueChangedEvent<ClientPricing.V2.NewClient>) => {
                return updateClientMutation.mutateAsync({
                  rowData: params.data,
                  oldValue: params.oldValue,
                  newValue: params.newValue,
                  updatedField: params.colDef.field!,
                })
              },
            }}
          />
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel>
          <AgGridWidget
            query={products}
            widget={{ exposed: true, className: 'h-full' }}
            widgetHeader={{ title: `Products${client_id ? ` - ${selectedClientName}` : ''}` }}
            grid={{
              rowData: products.data ?? [],
              columnDefs: productsColDefs,
              domLayout: 'normal',
              getContextMenuItems: getProductsContextMenuItems,
              getRowId: (params: GetRowIdParams<ClientPricing.V2.NewProduct>) => {
                return params.data.instrument_id?.toString()
              },
            }}
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    </Page>
  )
}
